import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useAppStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { redirect } from "@tanstack/react-router";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Login — PataTasks" },
      {
        name: "description",
        content:
          "Log in to your PataTasks account to access app and web testing, test submissions, and wallet.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: async () => {
    const store = useAppStore.getState();
    if (!store.authInitialized) {
      await store.initAuth();
    }
    if (store.isAuthenticated) {
      throw redirect({ to: "/" });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState("");
  const login = useAppStore((s) => s.login);
  const isLoading = useAppStore((s) => s.isLoading);
  const error = useAppStore((s) => s.error);
  const clearError = useAppStore((s) => s.clearError);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setFormError("");

    if (!identifier || !password) {
      setFormError("Please fill in all fields");
      return;
    }

    const trimmedIdentifier = identifier.trim();
    const normalizedDigits = trimmedIdentifier.replace(/\D/g, "");

    if (trimmedIdentifier.includes("@") && !/^\S+@\S+\.\S+$/.test(trimmedIdentifier)) {
      setFormError("Enter a valid email");
      return;
    } else if (
      !trimmedIdentifier.includes("@") &&
      !(normalizedDigits.length === 10 && normalizedDigits.startsWith("0"))
    ) {
      setFormError("Phone must be 10 digits and start with 0");
      return;
    }

    const success = await login(trimmedIdentifier, password);
    if (success) {
      toast.success("Welcome back!", { duration: 2000 });
      const store = useAppStore.getState();
      if (!store.user?.onboardingComplete) {
        navigate({ to: "/onboarding" });
      } else {
        navigate({ to: "/" });
      }
    } else {
      const msg = error || "Login failed";
      setFormError(msg);
      toast.error(msg, { duration: 3000 });
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-linear-to-br from-slate-50 via-blue-50/30 to-background px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-linear-to-br from-blue-700 to-indigo-700 text-white shadow-xl">
            <img
              src="/patataskslogo.png"
              alt="PataTasks Logo"
              className="h-12 w-12 rounded-full object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Welcome to PataTasks</h1>
          <p className="mt-1 text-sm text-slate-500">Log in to access online jobs and wallet</p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          {formError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {formError}
            </div>
          )}
          <div className="mb-4">
            <Label htmlFor="identifier" className="mb-1.5 text-slate-700">
              Email or Phone Number
            </Label>
            <Input
              id="identifier"
              type="text"
              inputMode="text"
              autoComplete="username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="Email or 0712345678"
              disabled={isLoading}
              className="bg-slate-50"
            />
          </div>
          <div className="mb-6">
            <Label htmlFor="password" className="mb-1.5 text-slate-700">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isLoading}
                className="bg-slate-50 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-2 text-slate-400"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Logging in..." : "Login"}
          </Button>
          <p className="mt-3 text-center text-sm">
            <Link to="/forgot-password" className="text-primary font-medium hover:underline">
              Forgot password?
            </Link>
          </p>
          <p className="mt-4 text-center text-sm text-slate-500">
            Don't have an account?{" "}
            <Link to="/signup" className="font-medium text-primary hover:underline">
              Create account
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
